import React, { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { submitLead } from '../lib/leads';

// Register a farmer group. The details go to the Imfuyo team and into Imfuyo Cover through submitLead.
const LIVESTOCK = ['Dairy goats', 'Meat goats', 'Dairy cows', 'Beef cattle', 'Pigs', 'Camels', 'Other'];
const TYPES = ['Self-help group', 'Chama', 'Livestock group', 'Other'];
const EMPTY = { groupName: '', groupType: 'Self-help group', county: '', members: '', contact: '', phone: '', email: '', consent: false };

export default function GroupRegister({ isDark, onNavigate }) {
  const [f, setF] = useState(EMPTY);
  const [stock, setStock] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  const head = isDark ? 'text-white' : 'text-[#14301A]';
  const body = isDark ? 'text-gray-300' : 'text-gray-600';
  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const input = `w-full h-12 rounded-xl border px-4 text-base outline-none focus:ring-2 focus:ring-[#285020] ${isDark ? 'bg-gray-950 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const label = `block text-sm font-semibold mb-1.5 ${head}`;

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const toggle = (s) => setStock((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (f.groupName.trim().length < 2) return setErr('Please give the name of your group.');
    if (f.county.trim().length < 2) return setErr('Please tell us the county where your group is based.');
    if (!(parseInt(f.members, 10) >= 2)) return setErr('Please tell us how many members your group has.');
    if (f.contact.trim().length < 2) return setErr('Please give the name of the contact person.');
    if (f.phone.replace(/\D/g, '').length < 9) return setErr('Please give a phone number we can reach, for example 07XX XXX XXX.');
    if (!f.consent) return setErr('Please tick the box so we can keep your group details.');
    setBusy(true);
    try {
      const livestock = stock.join(', ');
      await submitLead({
        name: f.contact,
        phone: f.phone,
        email: f.email,
        role: 'Farmer group or chama',
        subject: 'Group registration',
        source: 'group-registration',
        consent: f.consent,
        message: `Group registration: ${f.groupName.trim()} (${f.groupType}), ${f.county.trim()} County, ${parseInt(f.members, 10)} members. Livestock: ${livestock || 'not stated'}.`,
        extra: { groupName: f.groupName.trim(), groupType: f.groupType, county: f.county.trim(), memberCount: parseInt(f.members, 10), livestock },
      });
      setDone({ group: f.groupName.trim(), name: f.contact.trim().split(' ')[0], phone: f.phone.trim() });
      setF(EMPTY); setStock([]);
    } catch (ex) {
      setErr('Sorry, we could not send your details. Please try again, or message us on WhatsApp on +254 110 563 185.');
    } finally { setBusy(false); }
  };

  return (
    <section id="register-group" style={{ scrollMarginTop: 88 }} className={isDark ? 'bg-gray-950' : 'bg-[#F4F7F2]'}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <p className="text-sm font-semibold tracking-widest uppercase text-[#285020] mb-2">Step 1</p>
        <h2 className={`text-2xl sm:text-3xl font-bold mb-2 ${head}`}>Register your group</h2>
        <p className={`mb-8 ${body}`}>Tell us about your group. We keep these details safely with Imfuyo, and then your group can train together.</p>

        {done ? (
          <div className={`rounded-2xl border p-6 sm:p-8 ${card}`} role="status">
            <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#285020] text-white mb-4"><Check className="w-6 h-6" /></span>
            <h3 className={`text-xl font-bold mb-2 ${head}`}>{done.group} is registered with Imfuyo.</h3>
            <p className={`mb-6 ${body}`}>Thank you, {done.name}. Your group details have reached Imfuyo. Our team will call you on {done.phone} to confirm your members and plan your group training.</p>
            <p className={`text-sm font-semibold mb-3 uppercase tracking-widest text-[#285020]`}>Step 2: train your group</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => onNavigate('academy')} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D55103] hover:bg-[#B84402] text-white font-semibold">See the MbuziPlus Course <ArrowRight className="w-4 h-4" /></button>
              <a href="https://wa.me/254110563185?text=Hello%20Imfuyo%2C%20I%20have%20registered%20my%20group%20and%20would%20like%20to%20train%20together." target="_blank" rel="noopener noreferrer" className={`inline-flex items-center px-6 py-3 rounded-full border font-semibold ${isDark ? 'border-gray-600 text-white' : 'border-[#285020] text-[#285020]'}`}>Message us on WhatsApp</a>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className={`rounded-2xl border p-6 sm:p-8 grid gap-5 ${card}`} noValidate>
            <div><label className={label} htmlFor="gr-name">Group name</label><input id="gr-name" className={input} value={f.groupName} onChange={set('groupName')} autoComplete="organization" /></div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div><label className={label} htmlFor="gr-type">Type of group</label>
                <select id="gr-type" className={input} value={f.groupType} onChange={set('groupType')}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
              <div><label className={label} htmlFor="gr-county">County</label><input id="gr-county" className={input} value={f.county} onChange={set('county')} placeholder="For example, Kirinyaga" /></div>
            </div>
            <div><label className={label} htmlFor="gr-members">Number of members</label><input id="gr-members" className={input} type="number" min="2" inputMode="numeric" value={f.members} onChange={set('members')} /></div>
            <div>
              <span className={label}>Livestock your members keep</span>
              <div className="flex flex-wrap gap-2">
                {LIVESTOCK.map((s) => (
                  <button type="button" key={s} onClick={() => toggle(s)} aria-pressed={stock.includes(s)}
                    className={`px-4 h-11 rounded-full border text-sm font-semibold ${stock.includes(s) ? 'bg-[#285020] border-[#285020] text-white' : isDark ? 'border-gray-700 text-gray-200' : 'border-gray-300 text-gray-700'}`}>{s}</button>
                ))}
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div><label className={label} htmlFor="gr-contact">Contact person</label><input id="gr-contact" className={input} value={f.contact} onChange={set('contact')} autoComplete="name" /></div>
              <div><label className={label} htmlFor="gr-phone">Phone</label><input id="gr-phone" className={input} value={f.phone} onChange={set('phone')} inputMode="tel" autoComplete="tel" placeholder="07XX XXX XXX" /></div>
            </div>
            <div><label className={label} htmlFor="gr-email">Email (optional)</label><input id="gr-email" className={input} type="email" value={f.email} onChange={set('email')} autoComplete="email" /></div>
            <label className={`flex gap-3 items-start text-sm ${body}`}>
              <input type="checkbox" checked={f.consent} onChange={set('consent')} className="mt-1 w-5 h-5 accent-[#285020]" />
              <span>I agree that Imfuyo may keep these details and use them to contact me and to register my group on the Imfuyo platform. Each member decides what is shared, and with whom. <button type="button" onClick={() => onNavigate('privacy')} className="underline font-semibold text-[#285020]">Privacy and cookies</button></span>
            </label>
            {err && <p role="alert" className="text-sm font-semibold text-[#B42318]">{err}</p>}
            <button type="submit" disabled={busy} className="h-14 rounded-full bg-[#D55103] hover:bg-[#B84402] disabled:opacity-60 text-white text-lg font-bold inline-flex items-center justify-center gap-2">
              {busy ? 'Sending…' : <>Register my group <ArrowRight className="w-5 h-5" /></>}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
